import { Stack } from '@mui/material';
import {
    BooleanInput,
    SelectInput,
    TextInput,
    required,
    useRecordContext,
} from 'react-admin';
import ImageEditorField from '@app/misc/ImageEditorField';
import type { MissionTypeInput } from '@domain/missionType';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { useResource } from '@app/misc/useResource';
import { showModeReadOnlySx } from '@app/root/theme';

export const MissionTypeInputs = ({
    readOnly = false,
}: {
    readOnly?: boolean;
}) => {
    const record = useRecordContext<MissionTypeInput>();
    const { memberTypeLabels } = useConfigurationContext();
    const { data: iconSrc } = useResource(record?.iconId);

    return (
        <Stack gap={2} sx={readOnly ? showModeReadOnlySx : undefined}>
            <TextInput
                source="id"
                label="Id"
                disabled={!!record?.id || readOnly}
                helperText={
                    record?.id
                        ? 'Id cannot be changed when editing.'
                        : 'Unique identifier (e.g. Awareness_Stand, School).'
                }
                validate={record?.id ? undefined : required()}
            />
            <TextInput
                source="name"
                label="Name"
                validate={required()}
                readOnly={readOnly}
            />
            <ImageEditorField
                source="iconInput"
                height={120}
                emptyText="Upload icon"
                linkPosition="bottom"
                src={iconSrc ?? undefined}
                readOnly={readOnly}
                helperText="We recommend using an SVG type image, and square format (1:1 aspect ratio) for best results : https://www.svgrepo.com/"
            />
            <TextInput
                source="shortDescription"
                label="Short description"
                multiline
                minRows={2}
                readOnly={readOnly}
            />
            <SelectInput
                source="whoCanSee"
                label="Who can see"
                choices={memberTypeLabels.map(type => ({
                    id: type.value,
                    name: type.label,
                }))}
                validate={required()}
                readOnly={readOnly}
                helperText="'Volunteer' means any member of the WWF community will be able to see this mission type ; 'Main
                    Volunteer' means main volunteers and staff ; 'Staff' means only WWF employees"
            />
            <SelectInput
                source="whoCanParticipate"
                label="Who can participate"
                choices={memberTypeLabels.map(type => ({
                    id: type.value,
                    name: type.label,
                }))}
                validate={required()}
                readOnly={readOnly}
                helperText="'Volunteer' means any member of the WWF community will be able to register and participate in this mission type ; 'Main
                    Volunteer' means main volunteers and staff ; 'Staff' means only WWF employees"
            />
            <SelectInput
                source="whoCanCreate"
                label="Who can create"
                choices={memberTypeLabels.map(type => ({
                    id: type.value,
                    name: type.label,
                }))}
                validate={required()}
                readOnly={readOnly}
                helperText="'Volunteer' means any member of the WWF community will be able to create a mission with this mission type ; 'Main
                    Volunteer' means main volunteers and staff ; 'Staff' means only WWF employees"
            />
            <BooleanInput
                source="enabled"
                label="Enabled"
                defaultValue={true}
                readOnly={readOnly}
                helperText="If disabled, this mission type will not be available for new missions"
            />
        </Stack>
    );
};
